import { useCallback, useMemo, useState, type ReactNode } from "react";
import { theme, Flex, Row, Col } from "antd";
import type { GlobalToken } from "antd";
import { useTranslate } from "@refinedev/core";
import styled from "styled-components";
import { Label, Select, Toggle, radius } from "@libertify/ui";
import { PlaybookAddOnCard } from "../PlaybookAddOnCard";
import { usePlaybookDraft } from "../../context/PlaybookDraftContext";
import { useVideoConfigData } from "./hooks/useVideoConfigData";
import { VIDEO_DEFAULTS } from "./defaults";
import { CONFIG_ITEM } from "../../data/addons";
import type {
  VideoAspectRatio,
  VideoConfigDraft,
  VideoConfigOption,
  VideoConfigValue,
} from "./types";

export type SharedProps = {
  /** Controlled value. Falls back to internal state when omitted. */
  value?: VideoConfigDraft;
  /** Initial value when uncontrolled. */
  defaultValue?: VideoConfigDraft;
  /** Called with the full next value on any change. */
  onChange?: (value: VideoConfigValue) => void;
  /**
   * Credit cost shown in the header pill. Defaults to a placeholder until the
   * real per-config cost is threaded through
   */
  creditCost?: number;
};

export type VideoConfigCardViewProps = SharedProps & {
  durations: VideoConfigOption[];
  /** Org-preference defaults used to preselect any field the user hasn't set. */
  defaults?: VideoConfigDraft;
};

// Ids are the API `AspectRatio` values; labels keep their original locale keys.
const ASPECTS: {
  id: VideoAspectRatio;
  labelKey: string;
  ratio: string;
  w: number;
  h: number;
}[] = [
  { id: "desktop", labelKey: "landscape", ratio: "16:9", w: 34, h: 19 },
  { id: "mobile", labelKey: "portrait", ratio: "9:16", w: 19, h: 34 },
  { id: "tablet", labelKey: "tablet", ratio: "3:4", w: 24, h: 32 },
];

const withDefault = <T extends string | number>(
  value: T | null | undefined,
  options: VideoConfigOption[],
  preferred?: T | null
): T | undefined => {
  // "" and 0 both read as unset (0 is the API's "no duration" default).
  if (value) return value;
  if (preferred && options.some((option) => option.value === preferred)) {
    return preferred;
  }
  return options[0]?.value as T | undefined;
};

const Field = ({
  label,
  full,
  children,
}: {
  label: string;
  full?: boolean;
  children: ReactNode;
}) => (
  <Col xs={24} md={full ? 24 : 12}>
    <Flex vertical gap={8} align="flex-start" style={{ minWidth: 0 }}>
      <Label>{label}</Label>
      {children}
    </Flex>
  </Col>
);

export const VideoConfigCardView = ({
  value,
  defaultValue,
  onChange,
  // TODO(MED-6810): replace placeholder with the real per-config credit cost.
  creditCost = 600,
  durations,
  defaults,
}: VideoConfigCardViewProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();

  const [internal, setInternal] = useState<VideoConfigDraft>(() => ({
    ...defaultValue,
  }));

  const current = useMemo<VideoConfigDraft>(
    () => value ?? internal,
    [value, internal]
  );

  // Resolve the effective value from three layers, most specific first: the
  // user's own value, then the org-preference default, then a card-level
  // fallback. Used for BOTH display and the onChange payload, so what the user
  // sees selected always equals what the parent receives. Recomputes when the
  // async option lists / defaults arrive.
  // NOTE: defaults are not emitted on mount (we deliberately avoid setState in an
  // effect) — the parent only receives them once the user changes any field. A
  // consumer that submits without interaction must read `resolved`, not the raw
  // initial value.
  // The experience-wide fields (goal, prompt, brand kit, avatar, language)
  // moved to the general settings card with the backend's `General` add-on
  // type — see `GeneralSettingsConfigCard`.
  const resolved = useMemo<VideoConfigValue>(() => {
    const gen = current.generation_settings ?? {};
    const defaultsGen = defaults?.generation_settings ?? {};
    return {
      generation_settings: {
        duration:
          withDefault(
            gen.duration,
            durations,
            defaultsGen.duration ?? VIDEO_DEFAULTS.generation_settings.duration
          ) ?? 0,
        aspect_ratio:
          gen.aspect_ratio ??
          defaultsGen.aspect_ratio ??
          VIDEO_DEFAULTS.generation_settings.aspect_ratio,
        with_avatar:
          gen.with_avatar ??
          defaultsGen.with_avatar ??
          VIDEO_DEFAULTS.generation_settings.with_avatar,
        auto_select_music:
          gen.auto_select_music ??
          defaultsGen.auto_select_music ??
          VIDEO_DEFAULTS.generation_settings.auto_select_music,
        generate_brandkit:
          gen.generate_brandkit ??
          defaultsGen.generate_brandkit ??
          VIDEO_DEFAULTS.generation_settings.generate_brandkit,
      },
      // Hidden for now — waiting for backend confirmation that subtitles are
      // supported. Re-enable with the toggle below and the VIDEO_DEFAULTS /
      // VideoConfigValue entries.
      // subtitles:
      //   current.subtitles ?? defaults?.subtitles ?? VIDEO_DEFAULTS.subtitles,
    };
  }, [current, durations, defaults]);

  const update = useCallback(
    (patch: Partial<VideoConfigValue["generation_settings"]>) => {
      const next: VideoConfigValue = {
        generation_settings: {
          ...resolved.generation_settings,
          ...patch,
        },
        // subtitles: hidden for now (see the commented toggle below).
      };
      if (!value) setInternal(next);
      onChange?.(next);
    },
    [onChange, value, resolved]
  );

  const placeholder = t("createExperience.videoConfig.selectPlaceholder");

  return (
    <PlaybookAddOnCard
      itemId={CONFIG_ITEM.Video}
      title={t("createExperience.videoConfig.title")}
      credits={creditCost}
      description={t("createExperience.videoConfig.subtitle")}
    >
      <Row gutter={[48, 24]}>
        <Field label={t("createExperience.videoConfig.duration.label")}>
          <Select
            options={durations}
            value={resolved.generation_settings.duration}
            placeholder={placeholder}
            onChange={(next) => update({ duration: Number(next) })}
          />
        </Field>

        <Field label={t("createExperience.videoConfig.brandKit.generateToggle")}>
          <Toggle
            size="l"
            checked={resolved.generation_settings.generate_brandkit}
            onChange={(checked) => update({ generate_brandkit: checked })}
            aria-label={t("createExperience.videoConfig.brandKit.generateToggle")}
          />
        </Field>

        <Field label={t("createExperience.videoConfig.aspectRatio.label")} full>
          <Flex wrap gap={10} style={{ width: "100%" }}>
            {ASPECTS.map((aspect) => {
              const active =
                resolved.generation_settings.aspect_ratio === aspect.id;
              const name = t(
                `createExperience.videoConfig.aspectRatio.${aspect.labelKey}`
              );
              return (
                <AspectButton
                  key={aspect.id}
                  type="button"
                  $token={token}
                  $active={active}
                  aria-pressed={active}
                  aria-label={name}
                  onClick={() => update({ aspect_ratio: aspect.id })}
                >
                  <Flex align="center" justify="center" style={{ height: 36 }}>
                    <AspectIcon
                      $token={token}
                      $active={active}
                      $w={aspect.w}
                      $h={aspect.h}
                    />
                  </Flex>
                  <span
                    style={{
                      fontSize: token.fontSizeSM,
                      fontWeight: 600,
                      letterSpacing: "-0.01em",
                      color: active ? token.colorPrimary : token.colorText,
                    }}
                  >
                    {name}
                  </span>
                  <span
                    style={{
                      fontSize: token.fontSizeSM - 1,
                      fontWeight: 600,
                      letterSpacing: "0.02em",
                      color: token.colorTextTertiary,
                    }}
                  >
                    {aspect.ratio}
                  </span>
                </AspectButton>
              );
            })}
          </Flex>
        </Field>

        {/* Hidden for now — waiting for backend confirmation that subtitles
            are supported. Re-enable together with the subtitles entries in
            `resolved`/`update`, VideoConfigValue/VideoConfigDraft,
            VIDEO_DEFAULTS and useVideoConfigData's defaults. */}
        {/* <Field label={t("createExperience.videoConfig.subtitles")}>
          <Toggle
            size="l"
            checked={resolved.subtitles}
            onChange={(checked) => update({ subtitles: checked })}
            aria-label={t("createExperience.videoConfig.subtitles")}
          />
        </Field> */}

        <Field label={t("createExperience.videoConfig.backgroundMusic")}>
          <Toggle
            size="l"
            checked={resolved.generation_settings.auto_select_music}
            onChange={(checked) => update({ auto_select_music: checked })}
            aria-label={t("createExperience.videoConfig.backgroundMusic")}
          />
        </Field>

        <Field label={t("createExperience.videoConfig.showAvatar")}>
          <Toggle
            size="l"
            checked={resolved.generation_settings.with_avatar}
            onChange={(checked) => update({ with_avatar: checked })}
            aria-label={t("createExperience.videoConfig.showAvatar")}
          />
        </Field>
      </Row>
    </PlaybookAddOnCard>
  );
};

/**
 * Connects the video config view to the shared PlaybookDraft: the current value
 * lives in the context (keyed by the "Video" add-on), so it survives switching
 * between cards and marks the draft dirty on change.
 */
export const VideoConfigCard = () => {
  const { addonObject, updateAddonSettings } = usePlaybookDraft();
  const { durations, defaults } = useVideoConfigData();
  const value = addonObject.Video as VideoConfigDraft | undefined;

  return (
    <VideoConfigCardView
      value={value}
      onChange={(next) => updateAddonSettings(CONFIG_ITEM.Video, next)}
      durations={durations}
      defaults={defaults}
    />
  );
};

export default VideoConfigCard;

// ─── Styled components ───

const AspectButton = styled.button<{ $token: GlobalToken; $active: boolean }>`
  flex: 1 1 0;
  min-width: 120px;
  max-width: 180px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 14px 10px 12px;
  border-radius: ${radius.box}px;
  border: 1.5px solid
    ${({ $token, $active }) =>
      $active ? $token.colorPrimary : $token.colorBorder};
  background: ${({ $token, $active }) =>
    $active ? $token.colorPrimaryBg : $token.colorBgContainer};
  cursor: pointer;
  font-family: inherit;
  transition: all ${({ $token }) => $token.motionDurationMid} ease;

  &:hover {
    border-color: ${({ $token, $active }) =>
      $active ? $token.colorPrimary : $token.colorPrimaryHover};
  }

  &:focus-visible {
    outline: 2px solid ${({ $token }) => $token.colorPrimary};
    outline-offset: 2px;
  }
`;

const AspectIcon = styled.span<{
  $token: GlobalToken;
  $w: number;
  $h: number;
  $active: boolean;
}>`
  width: ${({ $w }) => $w}px;
  height: ${({ $h }) => $h}px;
  border-radius: 3px;
  border: 2px solid
    ${({ $token, $active }) =>
      $active ? $token.colorPrimary : $token.colorTextTertiary};
  background: ${({ $token, $active }) =>
    $active ? $token.colorPrimaryBorder : "transparent"};
`;
